/**
 * Confirm primary-contact change on edit submit (email invitation vs phone-setup link).
 */

import * as React from 'react';
import { Copy } from 'lucide-react';
import { BaseModal } from '@/components/common/modals/BaseModal';
import { Button } from '@/components/forms/Button';

/** Live primary kind that requires a credential modal. */
export type ContactChangeKind = 'email' | 'phone';
/** Confirm → API wait → copy link (other-user phone only). */
export type ContactChangePhase = 'confirm' | 'pending' | 'copy';

/** Props for {@link ContactChangeModal}. */
interface ContactChangeModalProps {
  open: boolean;
  kind: ContactChangeKind;
  ownProfile: boolean;
  phase: ContactChangePhase;
  phoneSetupLink?: string;
  onClose: () => void;
  onConfirm: () => void;
  onCopyLink: () => void;
}

/** Title / body / confirm label for the current kind and own vs other. */
function resolveContactChangeCopy(
  kind: ContactChangeKind,
  ownProfile: boolean,
): { title: string; description: string; confirmText: string } {
  if (kind === 'email' && ownProfile) {
    return {
      title: 'Changer votre email',
      description:
        'Votre email de connexion va changer. Un lien d’activation sera envoyé à la nouvelle adresse. Vous serez déconnecté et ne pourrez plus vous connecter tant que vous n’aurez pas activé le compte.',
      confirmText: 'Confirmer et me déconnecter',
    };
  }
  if (kind === 'email') {
    return {
      title: 'Changer l’email de connexion',
      description:
        'Un email d’activation va être envoyé à la nouvelle adresse. L’ancien identifiant ne fonctionnera plus. Le compte sera verrouillé jusqu’à l’activation.',
      confirmText: 'Confirmer et envoyer',
    };
  }
  if (ownProfile) {
    return {
      title: 'Changer votre téléphone',
      description:
        'Votre identifiant de connexion va passer sur ce numéro. Vous serez déconnecté puis redirigé pour saisir le code SMS.',
      confirmText: 'Confirmer et vérifier',
    };
  }
  return {
    title: 'Changer le téléphone de connexion',
    description:
      'L’identifiant Firebase passera sur ce numéro. Aucun SMS n’est envoyé depuis la plateforme : après enregistrement, copiez le lien de configuration et partagez-le.',
    confirmText: 'Confirmer',
  };
}

/** Primary-contact confirm modal used on edit submit. */
export const ContactChangeModal: React.FC<ContactChangeModalProps> = ({
  open,
  kind,
  ownProfile,
  phase,
  phoneSetupLink,
  onClose,
  onConfirm,
  onCopyLink,
}) => {
  const copy = resolveContactChangeCopy(kind, ownProfile);
  const isPending = phase === 'pending';
  const isCopy = phase === 'copy';
  const hideDismiss = isPending || isCopy;

  function renderFooter(): React.ReactNode {
    if (isCopy) {
      return (
        <div className="flex gap-2 justify-end w-full">
          <Button
            type="button"
            preset="modal-confirm"
            icon={Copy}
            onClick={onCopyLink}
            disabled={!phoneSetupLink}
          >
            Copier le lien de configuration
          </Button>
        </div>
      );
    }
    return (
      <div className="flex gap-2 justify-end w-full">
        <Button type="button" preset="modal-cancel" onClick={onClose} disabled={isPending}>
          Annuler
        </Button>
        <Button
          type="button"
          preset="modal-confirm"
          onClick={onConfirm}
          loading={isPending}
          disabled={isPending}
        >
          {copy.confirmText}
        </Button>
      </div>
    );
  }

  function renderBody(): React.ReactNode {
    if (!isCopy) return <div />;
    return <p className="text-sm text-muted-foreground break-all">{phoneSetupLink}</p>;
  }

  return (
    <BaseModal
      open={open}
      onClose={onClose}
      size="md"
      hideCloseButton={hideDismiss}
      closeOnOutsideClick={!hideDismiss}
      closeOnEscape={!hideDismiss}
      header={{
        title: isCopy ? 'Lien de configuration téléphone' : copy.title,
        titleSize: 'lg',
        titleWeight: 'semibold',
      }}
      description={
        isCopy
          ? 'Copiez ce lien et partagez-le. Le formulaire se fermera ensuite.'
          : copy.description
      }
      hideContentWrapper={!isCopy}
      footer={renderFooter()}
    >
      {renderBody()}
    </BaseModal>
  );
};
